import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    Typography,
    Card,
    CardContent,
    CardMedia,
    IconButton,
    Button,
    Grid,
    Divider,
    Chip,
    CircularProgress,
    Alert,
    Snackbar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    InputAdornment,
    Breadcrumbs,
    Link,
    Stack,
    Paper,
    Fade,
    Slide,
    Pagination,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Collapse,
    List,
    ListItem,
    ListItemText,
    Avatar
} from '@mui/material';
import {
    ShoppingBag,
    Visibility,
    ArrowBack,
    LocalShipping,
    Payment,
    CheckCircle,
    Cancel,
    Pending,
    Loop,
    Truck,
    Assignment,
    Refresh,
    FilterList,
    ExpandMore,
    ExpandLess,
    Receipt,
    Phone,
    LocationOn,
    CalendarToday
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import createAppTheme from '../theme/theme';
import translations from '../i18n/translations';
import AppHeader from '../components/AppHeader';
import MobileDrawer from '../components/MobileDrawer';
import AppFooter from '../components/AppFooter';
import axios from 'axios';

const OrderListScreen = () => {
    const [language, setLanguage] = useState(localStorage.getItem('lang') || 'en');
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [user, setUser] = useState(null);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        pages: 0
    });
    const [statusFilter, setStatusFilter] = useState('');
    const [expandedOrders, setExpandedOrders] = useState({});
    const [refreshing, setRefreshing] = useState(false);

    const navigate = useNavigate();
    const t = translations[language];
    const theme = createAppTheme(true, language === 'ar');

    // Status configuration
    const statusConfig = {
        pending: {
            color: 'warning',
            icon: <Pending />,
            label: t.orderPending || 'Pending'
        },
        confirmed: {
            color: 'info',
            icon: <CheckCircle />,
            label: t.orderConfirmed || 'Confirmed'
        },
        processing: {
            color: 'primary',
            icon: <Loop />,
            label: t.orderProcessing || 'Processing'
        },
        shipped: {
            color: 'secondary',
            icon: <LocalShipping />,
            label: t.orderShipped || 'Shipped'
        },
        delivered: {
            color: 'success',
            icon: <CheckCircle />,
            label: t.orderDelivered || 'Delivered'
        },
        cancelled: {
            color: 'error',
            icon: <Cancel />,
            label: t.orderCancelled || 'Cancelled'
        },
        refunded: {
            color: 'default',
            icon: <Receipt />,
            label: t.orderRefunded || 'Refunded'
        }
    };

    // Check authentication on mount
    useEffect(() => {
        const token = localStorage.getItem('token');
        const userData = localStorage.getItem('user');

        if (!token || !userData) {
            navigate('/store');
            return;
        }

        setUser(JSON.parse(userData));
        fetchOrders();
    }, [navigate, pagination.page, statusFilter]);

    // Fetch orders
    const fetchOrders = async (showRefresh = false) => {
        try {
            if (showRefresh) {
                setRefreshing(true);
            } else {
                setLoading(true);
            }

            const token = localStorage.getItem('token');

            if (!token) {
                navigate('/store');
                return;
            }

            const params = new URLSearchParams({
                page: pagination.page.toString(),
                limit: pagination.limit.toString()
            });

            if (statusFilter) {
                params.append('status', statusFilter);
            }

            const response = await axios.get(`https://dhahabjw.com/api/orders?${params.toString()}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.data.success) {
                setOrders(response.data.data || []);
                setPagination(prev => ({
                    ...prev,
                    ...response.data.pagination
                }));
            } else {
                throw new Error(response.data.error || 'Failed to fetch orders');
            }
        } catch (error) {
            console.error('Error fetching orders:', error);

            if (error.response?.status === 401 || error.response?.status === 403) {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                navigate('/store');
                return;
            }

            setSnackbar({
                open: true,
                message: error.response?.data?.error || 'Failed to load orders',
                severity: 'error'
            });
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    // Helper function to get localized product name
    const getLocalizedName = (product) => {
        if (typeof product.name === 'object' && product.name !== null) {
            return product.name[language] || product.name.en || '';
        }
        return product.name || '';
    };

    // Format date
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString(language === 'ar' ? 'ar-EG' : 'en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Toggle order expansion
    const toggleOrderExpansion = (orderId) => {
        setExpandedOrders(prev => ({
            ...prev,
            [orderId]: !prev[orderId]
        }));
    };

    // Handle status filter change
    const handleStatusFilterChange = (event) => {
        setStatusFilter(event.target.value);
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    // Handle page change
    const handlePageChange = (event, newPage) => {
        setPagination(prev => ({ ...prev, page: newPage }));
    };

    // Handle refresh
    const handleRefresh = () => {
        fetchOrders(true);
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    if (loading) {
        return (
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <Box sx={{ flexGrow: 1, bgcolor: 'background.default', minHeight: '100vh' }}>
                    <AppHeader
                        language={language}
                        setLanguage={setLanguage}
                        drawerOpen={drawerOpen}
                        setDrawerOpen={setDrawerOpen}
                        t={t}
                    />
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                        <Box textAlign="center">
                            <CircularProgress sx={{ color: 'primary.main', mb: 2 }} />
                            <Typography color="text.secondary">{t.loading}</Typography>
                        </Box>
                    </Box>
                    <MobileDrawer language={language} drawerOpen={drawerOpen} setDrawerOpen={setDrawerOpen} t={t} />
                </Box>
            </ThemeProvider>
        );
    }

    const renderDeliveryInfo = (order) => {
        if (order.delivery?.option === 'store_pickup') {
            return (
                <Card sx={{ bgcolor: 'rgba(255, 255, 255, 0.02)' }}>
                    <CardContent sx={{ p: 2 }}>
                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                            {t.pickupDetails || 'Pickup Details'}
                        </Typography>
                        <Stack spacing={1}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <LocationOn sx={{ fontSize: 16, color: 'primary.main' }} />
                                <Typography variant="body2">
                                    {t.storePickup || 'Store Pickup'}
                                </Typography>
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                                {t.location || 'Location'}: {order.delivery.storeLocation}
                            </Typography>
                            <Box display="flex" alignItems="center" gap={1}>
                                <Phone sx={{ fontSize: 16, color: 'primary.main' }} />
                                <Typography variant="body2">
                                    {order.delivery.pickupContactPhone}
                                </Typography>
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>
            );
        }

        // Home delivery
        const address = order.delivery?.address || order.shipping; // Fallback to shipping for backward compatibility
        if (!address) return null;

        return (
            <Card sx={{ bgcolor: 'rgba(255, 255, 255, 0.02)' }}>
                <CardContent sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        {t.shippingAddress || 'Delivery Address'}
                    </Typography>
                    <Stack spacing={1}>
                        <Box display="flex" alignItems="center" gap={1}>
                            <LocationOn sx={{ fontSize: 16, color: 'primary.main' }} />
                            <Typography variant="body2">
                                {address.type}, {address.buildingNumber} {address.buildingName}, {t.floor} {address.floor}, {t.apt} {address.apartmentNumber}
                            </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                            {address.streetNumber} {address.streetName}, {t.block} {address.block}, {t.avenue} {address.avenue}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t.area}: {address.area.name.en}
                        </Typography>
                        {address.company && (
                            <Typography variant="body2" color="text.secondary">
                                {t.company}: {address.company}
                            </Typography>
                        )}
                        {address.additionalDirections && (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                {address.additionalDirections}
                            </Typography>
                        )}
                        <Box display="flex" alignItems="center" gap={1}>
                            <Phone sx={{ fontSize: 16, color: 'primary.main' }} />
                            <Typography variant="body2">
                                {address.phone}
                            </Typography>
                        </Box>
                    </Stack>
                </CardContent>
            </Card>
        );
    };

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Box sx={{ flexGrow: 1, bgcolor: 'background.default', minHeight: '100vh' }}>
                <AppHeader
                    language={language}
                    setLanguage={setLanguage}
                    drawerOpen={drawerOpen}
                    setDrawerOpen={setDrawerOpen}
                    t={t}
                />

                <MobileDrawer language={language} drawerOpen={drawerOpen} setDrawerOpen={setDrawerOpen} t={t} />

                <Container maxWidth="xl" sx={{ py: 4 }}>
                    {/* Filters */}
                    <Card sx={{ mb: 3 }}>
                        <CardContent sx={{ p: 3 }}>
                            <Stack direction="row" alignItems="center" spacing={3}>
                                <FilterList sx={{ color: 'primary.main' }} />
                                <FormControl size="small" sx={{ minWidth: 200 }}>
                                    <InputLabel>{t.filterByStatus || 'Filter by Status'}</InputLabel>
                                    <Select
                                        value={statusFilter}
                                        label={t.filterByStatus || 'Filter by Status'}
                                        onChange={handleStatusFilterChange}
                                    >
                                        <MenuItem value="">{t.allOrders}</MenuItem>
                                        {Object.entries(statusConfig).map(([status, config]) => (
                                            <MenuItem key={status} value={status}>
                                                <Stack direction="row" alignItems="center" spacing={1}>
                                                    {config.icon}
                                                    <span>{config.label}</span>
                                                </Stack>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Stack>
                        </CardContent>
                    </Card>

                    {orders.length === 0 ? (
                        // Empty Orders
                        <Fade in={true}>
                            <Paper
                                sx={{
                                    p: 6,
                                    textAlign: 'center',
                                    background: 'linear-gradient(135deg, rgba(26, 26, 26, 0.8), rgba(30, 30, 30, 0.8))',
                                    borderRadius: 3,
                                    border: '1px solid rgba(255, 255, 255, 0.08)'
                                }}
                            >
                                <ShoppingBag sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
                                <Typography variant="h5" color="text.primary" gutterBottom>
                                    {statusFilter
                                        ? (t.noOrdersWithStatus || 'No orders found with this status')
                                        : (t.noOrders || 'No orders yet')
                                    }
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                                    {statusFilter
                                        ? (t.tryDifferentFilter || 'Try selecting a different status filter')
                                        : (t.noOrdersMessage || 'You haven\'t placed any orders yet. Start shopping to see your orders here.')
                                    }
                                </Typography>
                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={() => navigate('/store')}
                                    sx={{
                                        px: 4,
                                        bgcolor: 'primary.main',
                                        color: 'black',
                                        '&:hover': { bgcolor: 'primary.dark' }
                                    }}
                                >
                                    {t.startShopping || 'Start Shopping'}
                                </Button>
                            </Paper>
                        </Fade>
                    ) : (
                        // Orders List
                        <Stack spacing={3}>
                            {orders.map((order, index) => (
                                <Slide
                                    key={order._id}
                                    direction="up"
                                    in={true}
                                    timeout={300 + (index * 100)}
                                >
                                    <Card sx={{ overflow: 'visible' }}>
                                        <CardContent sx={{ p: 3 }}>
                                            {/* Order Header */}
                                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                                <Box>
                                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                                        {t.orderNumber || 'Order'} #{order.orderNumber}
                                                    </Typography>
                                                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
                                                        <Box display="flex" alignItems="center" gap={1}>
                                                            <CalendarToday sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                            <Typography variant="body2" color="text.secondary">
                                                                {formatDate(order.createdAt)}
                                                            </Typography>
                                                        </Box>
                                                        <Chip
                                                            icon={statusConfig[order.status]?.icon}
                                                            label={statusConfig[order.status]?.label || order.status}
                                                            color={statusConfig[order.status]?.color || 'default'}
                                                            size="small"
                                                        />
                                                    </Stack>
                                                </Box>

                                                <Box textAlign="right">
                                                    <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                                                        {order.pricing.totalAmount.toFixed(3)} KWD
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {order.items.length} {order.items.length === 1 ? t.item : t.items}
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            {/* Order Items Preview */}
                                            <Box sx={{ mb: 2 }}>
                                                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                                                    {order.items.slice(0, 3).map((item, idx) => (
                                                        <Box
                                                            key={idx}
                                                            sx={{
                                                                width: 60,
                                                                height: 60,
                                                                borderRadius: 1,
                                                                overflow: 'hidden',
                                                                position: 'relative'
                                                            }}
                                                        >
                                                            <CardMedia
                                                                component="img"
                                                                width="100%"
                                                                height="100%"
                                                                image={item.productId?.images?.find(img => img.isPrimary)?.url ||
                                                                    item.productId?.images?.[0]?.url ||
                                                                    '/placeholder-image.jpg'}
                                                                alt={item.productName}
                                                                sx={{ objectFit: 'cover' }}
                                                            />
                                                            {item.quantity > 1 && (
                                                                <Chip
                                                                    label={item.quantity}
                                                                    size="small"
                                                                    sx={{
                                                                        position: 'absolute',
                                                                        top: 2,
                                                                        right: 2,
                                                                        minWidth: 20,
                                                                        height: 20,
                                                                        fontSize: '0.7rem'
                                                                    }}
                                                                />
                                                            )}
                                                        </Box>
                                                    ))}
                                                    {order.items.length > 3 && (
                                                        <Box
                                                            sx={{
                                                                width: 60,
                                                                height: 60,
                                                                borderRadius: 1,
                                                                bgcolor: 'rgba(255, 255, 255, 0.1)',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center'
                                                            }}
                                                        >
                                                            <Typography variant="body2" color="text.secondary">
                                                                +{order.items.length - 3}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Stack>
                                            </Box>

                                            {/* Actions */}
                                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                                <Stack direction="row" spacing={2}>
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <Payment sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                        <Typography variant="body2" color="text.secondary">
                                                            {order.payment.method.toUpperCase()}
                                                        </Typography>
                                                        <Chip
                                                            label={order.payment.status}
                                                            size="small"
                                                            color={order.payment.status === 'completed' ? 'success' : 'warning'}
                                                        />
                                                    </Box>
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        {order.delivery?.option === 'store_pickup' ? (
                                                            <>
                                                                <Assignment sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {t.storePickup || 'Store Pickup'}
                                                                </Typography>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <LocalShipping sx={{ fontSize: 16, color: 'text.secondary' }} />
                                                                <Typography variant="body2" color="text.secondary">
                                                                    {t.homeDelivery || 'Home Delivery'}
                                                                </Typography>
                                                            </>
                                                        )}
                                                    </Box>
                                                </Stack>

                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    endIcon={expandedOrders[order._id] ? <ExpandLess /> : <ExpandMore />}
                                                    onClick={() => toggleOrderExpansion(order._id)}
                                                    sx={{
                                                        borderColor: 'primary.main',
                                                        color: 'primary.main',
                                                        '&:hover': {
                                                            borderColor: 'primary.dark',
                                                            bgcolor: 'rgba(212, 175, 55, 0.1)'
                                                        }
                                                    }}
                                                >
                                                    {expandedOrders[order._id] ? (t.hideDetails || 'Hide Details') : (t.viewDetails || 'View Details')}
                                                </Button>
                                            </Box>

                                            {/* Expanded Details */}
                                            <Collapse in={expandedOrders[order._id]}>
                                                <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                                                    <Grid container spacing={3}>
                                                        {/* Order Items */}
                                                        <Grid item xs={12} md={8}>
                                                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                                                {t.orderItems || 'Order Items'}
                                                            </Typography>
                                                            <Stack spacing={2}>
                                                                {order.items.map((item, idx) => (
                                                                    <Card key={idx} sx={{ bgcolor: 'rgba(255, 255, 255, 0.02)' }}>
                                                                        <CardContent sx={{ p: 2 }}>
                                                                            <Grid container spacing={2} alignItems="center">
                                                                                <Grid item xs={3}>
                                                                                    <CardMedia
                                                                                        component="img"
                                                                                        height="80"
                                                                                        image={item.productId?.images?.find(img => img.isPrimary)?.url ||
                                                                                            item.productId?.images?.[0]?.url ||
                                                                                            '/placeholder-image.jpg'}
                                                                                        alt={item.productName}
                                                                                        sx={{ objectFit: 'cover', borderRadius: 1 }}
                                                                                    />
                                                                                </Grid>
                                                                                <Grid item xs={6}>
                                                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                                                        {item.productName}
                                                                                    </Typography>
                                                                                    <Typography variant="body2" color="text.secondary">
                                                                                        {t.quantity || 'Quantity'}: {item.quantity}
                                                                                    </Typography>
                                                                                    <Typography variant="body2" color="text.secondary">
                                                                                        {t.unitPrice || 'Unit Price'}: {item.unitPrice.toFixed(3)} KWD
                                                                                    </Typography>
                                                                                </Grid>
                                                                                <Grid item xs={3} textAlign="right">
                                                                                    <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 600 }}>
                                                                                        {item.totalPrice.toFixed(3)} KWD
                                                                                    </Typography>
                                                                                </Grid>
                                                                            </Grid>
                                                                        </CardContent>
                                                                    </Card>
                                                                ))}
                                                            </Stack>
                                                        </Grid>

                                                        {/* Order Summary & Shipping */}
                                                        <Grid item xs={12} md={4}>
                                                            <Stack spacing={3}>
                                                                {/* Pricing Summary */}
                                                                <Card sx={{ bgcolor: 'rgba(255, 255, 255, 0.02)' }}>
                                                                    <CardContent sx={{ p: 2 }}>
                                                                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                                                            {t.pricingSummary || 'Pricing Summary'}
                                                                        </Typography>
                                                                        <Stack spacing={1}>
                                                                            <Box display="flex" justifyContent="space-between">
                                                                                <Typography variant="body2" color="text.secondary">
                                                                                    {t.subtotal || 'Subtotal'}
                                                                                </Typography>
                                                                                <Typography variant="body2">
                                                                                    {order.pricing.subtotal.toFixed(3)} KWD
                                                                                </Typography>
                                                                            </Box>
                                                                            <Box display="flex" justifyContent="space-between">
                                                                                <Typography variant="body2" color="text.secondary">
                                                                                    {order.delivery?.option === 'store_pickup' ? (t.pickupFee || 'Pickup Fee') : (t.delivery || 'Delivery')}
                                                                                </Typography>
                                                                                <Typography variant="body2" color="success.main">
                                                                                    {order.pricing.shipping === 0 ? (t.free || 'Free') : `${order.pricing.shipping.toFixed(3)} KWD`}
                                                                                </Typography>
                                                                            </Box>
                                                                            {order.pricing.discount > 0 && (
                                                                                <Box display="flex" justifyContent="space-between">
                                                                                    <Typography variant="body2" color="text.secondary">
                                                                                        {t.discount || 'Discount'}
                                                                                    </Typography>
                                                                                    <Typography variant="body2" color="success.main">
                                                                                        -{order.pricing.discount.toFixed(3)} KWD
                                                                                    </Typography>
                                                                                </Box>
                                                                            )}
                                                                            <Divider sx={{ my: 1 }} />
                                                                            <Box display="flex" justifyContent="space-between">
                                                                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                                                    {t.total || 'Total'}
                                                                                </Typography>
                                                                                <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 600 }}>
                                                                                    {order.pricing.totalAmount.toFixed(3)} KWD
                                                                                </Typography>
                                                                            </Box>
                                                                        </Stack>
                                                                    </CardContent>
                                                                </Card>

                                                                {renderDeliveryInfo(order)}
                                                            </Stack>
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            </Collapse>
                                        </CardContent>
                                    </Card>
                                </Slide>
                            ))}

                            {/* Pagination */}
                            {pagination.pages > 1 && (
                                <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
                                    <Pagination
                                        count={pagination.pages}
                                        page={pagination.page}
                                        onChange={handlePageChange}
                                        color="primary"
                                        size="large"
                                        sx={{
                                            '& .MuiPaginationItem-root': {
                                                color: 'text.primary',
                                                '&.Mui-selected': {
                                                    bgcolor: 'primary.main',
                                                    color: 'black'
                                                }
                                            }
                                        }}
                                    />
                                </Box>
                            )}
                        </Stack>
                    )}
                </Container>

                {/* Snackbar for notifications */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={4000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert
                        onClose={handleCloseSnackbar}
                        severity={snackbar.severity}
                        sx={{ width: '100%' }}
                    >
                        {snackbar.message}
                    </Alert>
                </Snackbar>

                <AppFooter t={t} />
            </Box>
        </ThemeProvider>
    );
};

export default OrderListScreen;